// ─── Billing client lib ─────────────────────────────────────────────────────
//
// Single point of contact between the React app and the Stripe-related
// API routes / Supabase billing view. Two exports:
//
//   useBillingState(companyId) — hook that fetches v_company_billing_state
//     for a company and returns { state, loading, refetch }. Refetches on
//     window focus and every 60 seconds so a webhook landing while the
//     operator's mid-page is reflected without a hard reload.
//
//   callBillingApi(path, body) — POST helper that attaches the operator's
//     Supabase JWT as Authorization: Bearer for the protected endpoints
//     (/api/stripe/create-checkout-session etc.). Surfaces a structured
//     { ok, status, body } so callers can branch on error codes like
//     'no_subscription_yet' vs surface generic copy.
//
// We deliberately don't memoize the billing state across components — the
// view is cheap (single row, indexed by company_id) and stale data here
// is the kind of bug that costs trust, so re-fetching is the right
// default.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';

// Single source of truth for the per-page billing query. Returns the
// view row + loading flag + a manual refetch (used by the success-page
// poll loop).
export function useBillingState(companyId) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks whether the component is still mounted across awaits.
  const cancelledRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!companyId) {
      setState(null);
      setLoading(false);
      return null;
    }
    const { data, error } = await supabase
      .from('v_company_billing_state')
      .select('*')
      .eq('company_id', companyId)
      .maybeSingle();
    if (cancelledRef.current) return null;
    if (error) {
      console.error('[useBillingState] fetch failed', error);
      setState(null);
      setLoading(false);
      return null;
    }
    setState(data || null);
    setLoading(false);
    return data;
  }, [companyId]);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    fetchOnce();
    return () => { cancelledRef.current = true; };
  }, [fetchOnce]);

  // Refetch on window focus + every 60s. The 60s interval is a sane
  // upper bound on staleness — if a webhook flipped status and the
  // operator is sitting on a page, we'll catch it within a minute even
  // without realtime.
  useEffect(() => {
    if (!companyId) return undefined;
    const onFocus = () => { fetchOnce(); };
    window.addEventListener('focus', onFocus);
    const interval = setInterval(fetchOnce, 60000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, [companyId, fetchOnce]);

  return { state, loading, refetch: fetchOnce };
}

// Helper for the protected billing endpoints. Caller passes a relative
// path (e.g. '/api/stripe/create-checkout-session') and a body; we attach
// the current Supabase access token as a Bearer Authorization header.
//
// Returns:
//   { ok: true, status, body }              on success
//   { ok: false, status, body, error }      on any non-2xx (still resolves;
//                                            doesn't throw)
//
// Surfaces structured errors via body.error so callers can pattern-match
// (e.g. 'no_subscription_yet' triggers the dev-mode CTA in the BillingSettingsTab).
export async function callBillingApi(path, body) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess && sess.session && sess.session.access_token;
  if (!token) {
    return { ok: false, status: 401, body: null, error: 'no_session' };
  }
  let r;
  try {
    r = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body || {}),
    });
  } catch (e) {
    return { ok: false, status: 0, body: null, error: 'network', message: e.message };
  }
  let parsed = null;
  try {
    parsed = await r.json();
  } catch (_) { /* non-JSON response — leave body null */ }
  if (!r.ok) {
    return {
      ok: false,
      status: r.status,
      body: parsed,
      error: (parsed && parsed.error) || 'http_error',
    };
  }
  return { ok: true, status: r.status, body: parsed };
}

// Convenience: fire the checkout flow + redirect. Used by Paywall,
// WelcomeModal, BillingSettingsTab. Returns void; caller-side state
// lives in those components.
export async function startCheckout(plan = 'monthly', { onError } = {}) {
  const result = await callBillingApi('/api/stripe/create-checkout-session', { plan });
  if (!result.ok || !result.body || !result.body.url) {
    if (onError) onError(result);
    return false;
  }
  window.location.href = result.body.url;
  return true;
}

// Convenience: open the Customer Portal. Returns the same structured
// shape so the caller can react to 'no_subscription_yet' (dev-mode case).
export async function openCustomerPortal({ onError } = {}) {
  const result = await callBillingApi('/api/stripe/create-portal-session', {});
  if (!result.ok || !result.body || !result.body.url) {
    if (onError) onError(result);
    return false;
  }
  window.location.href = result.body.url;
  return true;
}

// Manual reconciliation — called by the /billing/success poll fallback.
export async function syncBillingStatus() {
  return callBillingApi('/api/stripe/sync-status', {});
}
